import {
  Alert,
  Card,
  CardBorderFill,
  Chip,
  EmptyState,
  Group,
  SearchInput,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@assetwatch/ui";
import { getRouteApi } from "@tanstack/react-router";
import { useState } from "react";
import { mlApiErrorMessage, mlApiErrorStatus } from "../../api/mlApi";
import type { HubHealthCost, ListFacilitiesResponse } from "./api";
import { FacilityDetail } from "./FacilityDetail";
import { filterFacilities, formatCount, scoreTone, shortId } from "./lib";
import { useHubHealthFacilities, useHubHealthFacility } from "./queries";

const routeApi = getRouteApi("/_app/hub-health");

/**
 * What actually went wrong: the endpoint's own `{"error": "..."}` first, then
 * the status-specific hint, then the raw error. A 200 that failed validation
 * has none of the first two.
 */
function errorSubtext(error: unknown): string {
  const apiMessage = mlApiErrorMessage(error);
  if (apiMessage === "forbidden") {
    // The endpoint's allowlist, not IAM: the request was signed and reached it.
    return "Your Cognito identity isn't on the ML API's tester allowlist yet.";
  }
  if (apiMessage) {
    return apiMessage;
  }

  if (mlApiErrorStatus(error) === 403) {
    return "The request was rejected before it reached the endpoint — check that it's signed through postMlApi.";
  }

  return error instanceof Error
    ? error.message
    : "The response didn't match the expected shape.";
}

function formatUsd(usd: number): string {
  if (usd > 0 && usd < 0.0001) {
    return "<$0.0001";
  }
  return `$${usd.toFixed(4)}`;
}

// Surfaced for the same reason as on the other pages: each call scans the data
// lake. An estimate of data scanned, not a bill.
function QueryCostSummary({
  cost,
}: {
  cost: HubHealthCost | null | undefined;
}) {
  if (!cost) {
    return null;
  }

  const parts = [
    cost.usd === null || cost.usd === undefined
      ? "cost unavailable"
      : formatUsd(cost.usd),
    cost.mb_scanned === null || cost.mb_scanned === undefined
      ? "MB unavailable"
      : `${cost.mb_scanned.toLocaleString(undefined, {
          maximumFractionDigits: 1,
        })} MB scanned`,
    cost.seconds === null || cost.seconds === undefined
      ? null
      : `${cost.seconds.toFixed(2)}s`,
  ].filter((part): part is string => part !== null);

  return (
    <Text size="sm" tone="subtle">
      <span title="Estimated cost of the data scanned by this query. Excludes compute; not a bill.">
        Scan estimate: {parts.join(" · ")}
      </span>
    </Text>
  );
}

/**
 * The facility picker down the left of the page: every facility, worst first,
 * searchable by id. The whole fleet is already in memory, so searching is local
 * and free.
 *
 * This is the page's own rail, not the app sidebar — the sidebar is shared
 * chrome that lists pages, and a page never adds to it.
 */
function FacilityRail({
  list,
  selectedId,
  onOpen,
}: {
  list: ListFacilitiesResponse;
  selectedId: string | undefined;
  onOpen: (id: string) => void;
}) {
  const [search, setSearch] = useState("");
  const facilities = filterFacilities(list.facilities, search);

  return (
    <Stack gap={12}>
      <Stack gap={2}>
        <Title size="md" weight="bold">
          Facilities
        </Title>
        <Text size="sm" tone="subtle">
          {facilities.length.toLocaleString()} of{" "}
          {list.total_facilities.toLocaleString()} · {list.date}
        </Text>
        <Text size="sm" tone="subtle">
          {list.unassigned_hubs.toLocaleString()} hubs in no facility
        </Text>
        <QueryCostSummary cost={list.cost} />
      </Stack>
      <SearchInput
        aria-label="Search facilities"
        onChange={setSearch}
        placeholder="Facility id"
        value={search}
      />
      <nav aria-label="Facilities, worst signal health first">
        <div className="max-h-[calc(100vh-18rem)] overflow-y-auto">
          <Stack gap={4}>
            {facilities.length === 0 ? (
              <Text size="sm" tone="subtle">
                {list.facilities.length === 0
                  ? "No facilities were scored for this snapshot."
                  : "No facility id matches that search."}
              </Text>
            ) : null}
            {facilities.map((facility) => {
              const { summary } = facility;
              const selected = facility.id === selectedId;
              return (
                <Card
                  aria-current={selected ? "true" : undefined}
                  key={facility.id}
                  onClick={() => onOpen(facility.id)}
                  padding="compact"
                  title={facility.id}
                >
                  {selected ? <CardBorderFill tone="brand" /> : null}
                  <Group gap={8} justify="between" wrap="nowrap">
                    <Stack gap={2}>
                      <Text size="sm" weight="medium">
                        {shortId(facility.id)}
                      </Text>
                      <Text size="sm" tone="subtle">
                        {formatCount(summary.hubs)} HUBS ·{" "}
                        {summary.band.toUpperCase()}
                      </Text>
                    </Stack>
                    <Chip
                      label={formatCount(summary.health)}
                      tone={scoreTone(summary.health)}
                    />
                  </Group>
                </Card>
              );
            })}
          </Stack>
        </div>
      </nav>
    </Stack>
  );
}

function DetailSection({
  selectedId,
  date,
}: {
  selectedId: string | undefined;
  date: string;
}) {
  // Always the list's date, so the detail is the same snapshot as the list.
  const facilityQuery = useHubHealthFacility(selectedId, date);

  if (!selectedId) {
    return (
      <EmptyState
        description="Choose a facility on the left to see its hubs, worst first. Signal health is a weighted mean of six factors: timeliness, utilisation, delivery, redundancy, BLE health and schedule efficiency. A factor a hub couldn’t be scored on is left out, not counted as zero."
        icon="signal"
        title="Pick a facility"
      />
    );
  }

  if (facilityQuery.isError) {
    return (
      <Alert
        subtext={errorSubtext(facilityQuery.error)}
        title="Couldn't load this facility"
        tone="negative"
      />
    );
  }

  if (facilityQuery.isPending) {
    return (
      <Stack gap={16}>
        <Text size="sm" tone="subtle">
          Loading hubs for facility {selectedId}…
        </Text>
        <Skeleton height={160} radius="md" width="100%" />
        <Skeleton height={320} radius="md" width="100%" />
      </Stack>
    );
  }

  const detail = facilityQuery.data;

  // The backend's 404: the facility has no scored hubs on this date.
  if (!detail) {
    return (
      <Alert
        subtext={`Facility ${selectedId} has no scored hubs on ${date}.`}
        title="No hubs to show"
        tone="warning"
      />
    );
  }

  return (
    <Stack gap={8}>
      <Group justify="end">
        <QueryCostSummary cost={detail.cost} />
      </Group>
      <FacilityDetail response={detail} />
    </Stack>
  );
}

export function HubHealthScreen() {
  // The open facility lives in the URL, so a view can be shared or bookmarked.
  const { facility: selectedId } = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const facilitiesQuery = useHubHealthFacilities();
  const list = facilitiesQuery.data;

  function openFacility(id: string) {
    navigate({ search: { facility: id } });
  }

  if (facilitiesQuery.isError) {
    return (
      <Alert
        subtext={errorSubtext(facilitiesQuery.error)}
        title="Couldn't load facilities"
        tone="negative"
      />
    );
  }

  if (!list) {
    return (
      <Stack gap={16}>
        <Text size="sm" tone="subtle">
          Loading the fleet — one Athena scan, usually a few seconds.
        </Text>
        <Skeleton height={400} radius="md" width="100%" />
      </Stack>
    );
  }

  return (
    <div
      className="grid gap-(--spacing-24)"
      style={{ gridTemplateColumns: "18rem minmax(0, 1fr)" }}
    >
      <div className="sticky top-0 self-start">
        <FacilityRail
          list={list}
          onOpen={openFacility}
          selectedId={selectedId}
        />
      </div>
      <DetailSection date={list.date} selectedId={selectedId} />
    </div>
  );
}
